import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, X, Phone, MessageSquare, ArrowRightLeft, Wrench, ArrowLeft, Ban, Loader2 } from "lucide-react";
import { useCreateCustomTool, useCreateNativeTool, useNativeTools } from "@/services/tool.query";
import { type AgentTool, END_CALL_DESCRIPTION } from "@/services/tool.service";
import { SimplePhoneInput } from "@/components/ui/simple-phone-input";
import { sanitizeToolName, sanitizeParameterName } from "@/utils/tool-name-utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToolOptionsFields } from "./ToolOptionsFields";
import { DEFAULT_TOOL_OPTIONS, toolOptionColumns, toolSaveErrorMessage, type LiveRuntime, type ToolOptions } from "@/utils/tool-options";
import { customToolErrors, dialableNumber, smsToolErrors, transferToolErrors, type ToolFormErrors } from "./tool-form";
import { useFieldErrors } from "./use-field-errors";

interface AddToolDialogProps {
  agentId: string;
  /** The runtime of the editor's current (unsaved) model. */
  runtime: LiveRuntime;
}

type ToolType = "custom" | "end_call" | "send_sms" | "transfer_call" | null;

export const AddToolDialog = ({ agentId, runtime }: AddToolDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ToolType>(null);
  
  // Common fields
  const [toolName, setToolName] = useState("");
  const [description, setDescription] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [httpMethod, setHttpMethod] = useState("GET");
  const [headers, setHeaders] = useState<Array<{ key: string; value: string }>>([
    { key: "", value: "" },
  ]);
  const [parametersJson, setParametersJson] = useState("");
  const [timeoutSeconds, setTimeoutSeconds] = useState(30);
  const [toolOptions, setToolOptions] = useState<ToolOptions>(DEFAULT_TOOL_OPTIONS);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Send SMS fields
  const [smsContentType, setSmsContentType] = useState<"prompt" | "static">("prompt");
  const [smsStaticMessage, setSmsStaticMessage] = useState("");
  const [smsTwilioAccountSid, setSmsTwilioAccountSid] = useState("");
  const [smsTwilioAuthToken, setSmsTwilioAuthToken] = useState("");
  const [smsTwilioFromNumber, setSmsTwilioFromNumber] = useState("");

  // Transfer Call fields
  const [transferMode, setTransferMode] = useState<"static" | "dynamic">("static");
  const [transferDestination, setTransferDestination] = useState("");
  const [transferType, setTransferType] = useState<"cold" | "warm">("cold");
  const [transferDynamicPrompt, setTransferDynamicPrompt] = useState("");

  // Strip any baked-in execution-protocol block from a description so that
  // old tools (which had a protocol appended) get cleaned the next time they
  // are displayed or saved. Removes both the "[CRITICAL PROTOCOL ...]" block
  // and the "[BACKGROUND PROCESS: SILENT EXECUTION]" block.
  const stripProtocol = (desc: string): string => {
    if (!desc) return desc;
    return desc
      .replace(/\n*\[CRITICAL PROTOCOL: ATOMIC ACTION REQUIRED\][\s\S]*?Speak, then execute\.\s*/g, "")
      .replace(/\n*\[BACKGROUND PROCESS: SILENT EXECUTION\][\s\S]*?(?:Immediate execution is required\.|$)\s*/g, "")
      .trim();
  };

  const createCustomTool = useCreateCustomTool();
  const createNativeTool = useCreateNativeTool();
  // The built-ins only, and all of them: the list behind this dialog is a page,
  // so a native tool further down it would otherwise look free to add.
  const { data: nativeTools } = useNativeTools(agentId);

  // Check which native tools already exist
  const hasEndCall = nativeTools?.some(t => t.tool_name === 'end_call') || false;
  const hasSendSms = nativeTools?.some(t => t.tool_name === 'send_sms') || false;
  const hasTransferCall = nativeTools?.some(t => t.tool_name === 'transfer_call') || false;

  const handleTypeSelection = (type: ToolType) => {
    setSelectedType(type);
    // A new pick starts from Wait, so a "Keep talking" left behind by the back
    // arrow never reaches the next tool.
    setToolOptions(DEFAULT_TOOL_OPTIONS);
    setSaveError(undefined);
    // Errors shown for an earlier pick do not carry over to this one.
    resetFieldErrors();

    // Prefill based on type. A native tool's endpoint, method, headers and
    // parameters are set by the server.
    if (type === "end_call") {
      setToolName("end_call");
      setDescription(END_CALL_DESCRIPTION);
    } else if (type === "send_sms") {
      setToolName("send_sms");
      setDescription("");
    } else if (type === "transfer_call") {
      setToolName("transfer_call");
      setDescription("");
    } else {
      // Reset for custom
      setToolName("");
      setDescription("");
      setEndpointUrl("");
      setHttpMethod("GET");
      setParametersJson("");
    }
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: "key" | "value", value: string) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const resetForm = () => {
    setSelectedType(null);
    setToolName("");
    resetFieldErrors();
    setDescription("");
    setEndpointUrl("");
    setHttpMethod("GET");
    setHeaders([{ key: "", value: "" }]);
    setParametersJson("");
    setTimeoutSeconds(30);
    setToolOptions(DEFAULT_TOOL_OPTIONS);
    setSaveError(undefined);
    setSmsContentType("prompt");
    setSmsStaticMessage("");
    setSmsTwilioAccountSid("");
    setSmsTwilioAuthToken("");
    setSmsTwilioFromNumber("");
    setTransferMode("static");
    setTransferDestination("");
    setTransferType("cold");
    setTransferDynamicPrompt("");
  };

  const errors: ToolFormErrors =
    selectedType === "custom"
      ? customToolErrors({ toolName, endpointUrl, parametersJson, timeoutSeconds })
      : selectedType === "send_sms"
        ? smsToolErrors({
            contentType: smsContentType,
            staticMessage: smsStaticMessage,
            accountSid: smsTwilioAccountSid,
            authToken: smsTwilioAuthToken,
            fromNumber: smsTwilioFromNumber,
            timeoutSeconds,
          })
        : selectedType === "transfer_call"
          ? transferToolErrors({
              mode: transferMode,
              destination: transferDestination,
              dynamicPrompt: transferDynamicPrompt,
              description,
            })
          : {};
  const { field, error, check, reset: resetFieldErrors } = useFieldErrors(errors);

  const handleSubmit = async () => {
    // A refusal from an earlier try no longer applies to what is on screen now.
    setSaveError(undefined);
    // Nothing the server would refuse is sent: the reasons show under their fields.
    if (!check(contentRef.current)) return;
    setSaving(true);

    const headersObject = headers.reduce((acc, header) => {
      if (header.key.trim()) {
        acc[header.key] = header.value;
      }
      return acc;
    }, {} as Record<string, string>);

    let parameters: AgentTool["parameters"] = null;

    // The name, the JSON and the parameter names passed the check above, so the
    // parse below cannot fail and yields an object.
    if (selectedType === "custom" && parametersJson.trim()) {
      parameters = JSON.parse(parametersJson);


      // Sanitize parameter names
      if (parameters.properties) {
        const sanitizedProperties: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(parameters.properties)) {
          const sanitizedKey = sanitizeParameterName(key);
          sanitizedProperties[sanitizedKey] = value;
        }
        parameters.properties = sanitizedProperties;

        // Also sanitize required array if it exists
        if (Array.isArray(parameters.required)) {
          parameters.required = parameters.required.map((req: string) => sanitizeParameterName(req));
        }
      }
    }

    // Build the saved description: cleaned base description plus the dynamic
    // prompt (for dynamic transfers). No execution-protocol block is appended,
    // and any baked-in protocol from older tools is stripped out.
    const cleanDesc = stripProtocol(description.trim());
    let finalDescription = cleanDesc;
    if (selectedType === "transfer_call" && transferMode === "dynamic") {
      finalDescription = cleanDesc
        ? `${cleanDesc}\n\n[DYNAMIC PROMPT]\n${transferDynamicPrompt}`
        : `[DYNAMIC PROMPT]\n${transferDynamicPrompt}`;
    }

    // The server adds the tool to the agent's toolIds, so nothing follows the create.
    try {
      if (selectedType === "custom") {
        await createCustomTool.mutateAsync({
          agent_id: agentId,
          tool_name: sanitizeToolName(toolName),
          description: finalDescription || null,
          endpoint_url: endpointUrl,
          http_method: httpMethod,
          headers: headersObject,
          parameters,
          timeout_seconds: timeoutSeconds,
          ...toolOptionColumns(toolOptions),
        });
      } else if (selectedType) {
        await createNativeTool.mutateAsync({
          agent_id: agentId,
          tool_name: selectedType,
          description: finalDescription,
          ...(selectedType === "send_sms" && {
            timeout_seconds: timeoutSeconds,
            sms_content_type: smsContentType,
            sms_static_message: smsContentType === "static" ? smsStaticMessage : null,
            twilio_account_sid: smsTwilioAccountSid.trim() || null,
            twilio_auth_token: smsTwilioAuthToken.trim() || null,
            twilio_from_number: smsTwilioFromNumber.trim() || null,
          }),
          ...(selectedType === "transfer_call" && {
            transfer_call_mode: transferMode,
            transfer_call_destination: transferMode === "static" ? dialableNumber(transferDestination) : null,
            transfer_call_type: transferType,
          }),
        });
      }
      setOpen(false);
      resetForm();
    } catch (error) {
      // The hook toasts it too; the dialog stays open with the reason in it.
      setSaveError(toolSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    // A save in flight finishes in view: closing now would hide whether it worked.
    if (!isOpen && saving) return;
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="tool-add">
          <Plus className="w-4 h-4 mr-2" />
          Add Tool
        </Button>
      </DialogTrigger>
      <DialogContent ref={contentRef} className="max-w-2xl max-h-[80vh] overflow-y-auto [&_[aria-invalid=true]]:border-destructive">
        <DialogHeader>
          <DialogTitle>
            {selectedType ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedType(null)} disabled={saving} aria-label="Back to tool types">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                {selectedType === "custom" ? "Add Custom Tool" : 
                 selectedType === "end_call" ? "Add End Call Tool" :
                 selectedType === "send_sms" ? "Add Send SMS Tool" :
                 "Add Transfer Call Tool"}
              </div>
            ) : (
              "Select Tool Type"
            )}
          </DialogTitle>
          <DialogDescription>
            {selectedType 
              ? "Configure the tool settings below."
              : "Choose the type of tool you want to add to your agent."}
          </DialogDescription>
        </DialogHeader>

        {!selectedType ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="outline"
                      className={`h-32 w-full flex flex-col items-center justify-center gap-3 transition-all ${
                        hasEndCall 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:border-primary hover:bg-primary/5'
                      }`}
                      onClick={() => handleTypeSelection("end_call")}
                      data-testid="tool-type-end-call"
                      disabled={hasEndCall}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasEndCall ? 'bg-muted' : 'bg-red-100'}`}>
                        {hasEndCall ? <Ban className="w-5 h-5 text-muted-foreground" /> : <Phone className="w-5 h-5 text-red-600" />}
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">End Call</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {hasEndCall ? 'Already added' : 'End the conversation'}
                        </div>
                      </div>
                    </Button>
                  </div>
                </TooltipTrigger>
                {hasEndCall && (
                  <TooltipContent>
                    <p>This tool is already configured for this agent</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="outline"
                      className={`h-32 w-full flex flex-col items-center justify-center gap-3 transition-all ${
                        hasSendSms 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:border-primary hover:bg-primary/5'
                      }`}
                      onClick={() => handleTypeSelection("send_sms")}
                      data-testid="tool-type-send-sms"
                      disabled={hasSendSms}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasSendSms ? 'bg-muted' : 'bg-blue-100'}`}>
                        {hasSendSms ? <Ban className="w-5 h-5 text-muted-foreground" /> : <MessageSquare className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Send SMS</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {hasSendSms ? 'Already added' : 'Send text messages'}
                        </div>
                      </div>
                    </Button>
                  </div>
                </TooltipTrigger>
                {hasSendSms && (
                  <TooltipContent>
                    <p>This tool is already configured for this agent</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="outline"
                      className={`h-32 w-full flex flex-col items-center justify-center gap-3 transition-all ${
                        hasTransferCall 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:border-primary hover:bg-primary/5'
                      }`}
                      onClick={() => handleTypeSelection("transfer_call")}
                      data-testid="tool-type-transfer-call"
                      disabled={hasTransferCall}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasTransferCall ? 'bg-muted' : 'bg-green-100'}`}>
                        {hasTransferCall ? <Ban className="w-5 h-5 text-muted-foreground" /> : <ArrowRightLeft className="w-5 h-5 text-green-600" />}
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">Transfer Call</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {hasTransferCall ? 'Already added' : 'Transfer to another number'}
                        </div>
                      </div>
                    </Button>
                  </div>
                </TooltipTrigger>
                {hasTransferCall && (
                  <TooltipContent>
                    <p>This tool is already configured for this agent</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <Button
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-3 hover:border-primary hover:bg-primary/5 transition-all"
              onClick={() => handleTypeSelection("custom")}
              data-testid="tool-type-custom"
            >
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-center">
                <div className="font-semibold">Custom Tool</div>
                <div className="text-xs text-muted-foreground mt-1">Create your own tool</div>
              </div>
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            
            {/* Show standard name/desc for custom, or just description for native */}
            {selectedType === "custom" && (
              <div className="space-y-2">
                <Label htmlFor="tool-name">Tool Name *</Label>
                <Input
                  id="tool-name"
                  data-testid="tool-name"
                  placeholder="e.g., weather_api, database_query"
                  value={toolName}
                  onChange={(e) => setToolName(e.target.value)}
                  {...field("toolName", "tool-name")}
                />
                {error("toolName", "tool-name")}
                <p className="text-xs text-muted-foreground">
                  Must start with a letter or underscore. Use underscores instead of spaces (e.g., weather_api).
                </p>
              </div>
            )}

            {/* Hide general description for transfer_call - it has its own in the native section */}
            {selectedType !== "transfer_call" && (
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="tool-description"
                  placeholder="Describe what this tool does..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Describe when and how the agent should use this tool.
                </p>
              </div>
            )}

            {/* Native Tool Specific Configurations */}
            {selectedType === "send_sms" && (
              <div className="space-y-4 border-l-2 border-primary/20 pl-4 my-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <RadioGroup value={smsContentType} onValueChange={(v: "prompt" | "static") => setSmsContentType(v)}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="prompt" id="prompt" />
                      <Label htmlFor="prompt">Dynamic (Prompt User)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="static" id="static" />
                      <Label htmlFor="static">Static Message</Label>
                    </div>
                  </RadioGroup>
                </div>

                {smsContentType === "static" && (
                  <div className="space-y-2">
                    <Label htmlFor="static-message">Static Message</Label>
                    <Textarea
                      id="static-message"
                      placeholder="Enter the message to send..."
                      value={smsStaticMessage}
                      onChange={(e) => setSmsStaticMessage(e.target.value)}
                      {...field("smsStaticMessage", "static-message")}
                    />
                    {error("smsStaticMessage", "static-message")}
                  </div>
                )}

                <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                  <div className="space-y-1">
                    <Label>Twilio Credentials</Label>
                    <p className="text-xs text-muted-foreground">
                      Required for numbers imported via SIP trunking (LiveKit), which carry no
                      Twilio API credentials. Leave empty to use the phone number's or
                      workspace's Twilio credentials.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sms-twilio-sid">Account SID</Label>
                    <Input
                      id="sms-twilio-sid"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      value={smsTwilioAccountSid}
                      onChange={(e) => setSmsTwilioAccountSid(e.target.value)}
                      {...field("twilioAccountSid", "sms-twilio-sid")}
                    />
                    {error("twilioAccountSid", "sms-twilio-sid")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sms-twilio-token">Auth Token</Label>
                    <Input
                      id="sms-twilio-token"
                      type="password"
                      placeholder="Your Twilio auth token"
                      value={smsTwilioAuthToken}
                      onChange={(e) => setSmsTwilioAuthToken(e.target.value)}
                      {...field("twilioAuthToken", "sms-twilio-token")}
                    />
                    {error("twilioAuthToken", "sms-twilio-token")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sms-twilio-from">From Number (Optional)</Label>
                    <Input
                      id="sms-twilio-from"
                      placeholder="+15551234567"
                      value={smsTwilioFromNumber}
                      onChange={(e) => setSmsTwilioFromNumber(e.target.value)}
                      {...field("twilioFromNumber", "sms-twilio-from")}
                    />
                    {error("twilioFromNumber", "sms-twilio-from")}
                    <p className="text-xs text-muted-foreground">
                      SMS is sent from this number. Leave empty to send from the call's own
                      number — it must be SMS-capable in the Twilio account above.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedType === "transfer_call" && (
              <div className="space-y-4 border-l-2 border-primary/20 pl-4 my-4">
                <div className="space-y-2">
                  <Label htmlFor="transfer-description">Transfer Description</Label>
                  <Textarea
                    id="transfer-description"
                    placeholder="Describe when the agent should offer to transfer calls..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Describe when and how the agent should transfer calls.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Transfer Mode</Label>
                  <RadioGroup value={transferMode} onValueChange={(v: "static" | "dynamic") => setTransferMode(v)}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="static" id="mode-static" />
                      <Label htmlFor="mode-static" className="font-normal cursor-pointer">Static</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dynamic" id="mode-dynamic" />
                      <Label htmlFor="mode-dynamic" className="font-normal cursor-pointer">Dynamic</Label>
                    </div>
                  </RadioGroup>
                </div>

                {transferMode === "static" && (
                  <div className="space-y-2">
                    <Label htmlFor="destination">Phone Number *</Label>
                    <SimplePhoneInput
                      id="destination"
                      placeholder="1234567890"
                      value={transferDestination}
                      onChange={(value) => setTransferDestination(value)}
                      {...field("transferDestination", "destination")}
                    />
                    {error("transferDestination", "destination")}
                  </div>
                )}

                {transferMode === "dynamic" && (
                  <div className="space-y-2">
                    <Label htmlFor="dynamic-prompt">Dynamic Prompt *</Label>
                    <Textarea
                      id="dynamic-prompt"
                      placeholder="Instructions for determining the transfer destination dynamically..."
                      value={transferDynamicPrompt}
                      onChange={(e) => setTransferDynamicPrompt(e.target.value)}
                      rows={4}
                      {...field("transferDynamicPrompt", "dynamic-prompt")}
                    />
                    {error("transferDynamicPrompt", "dynamic-prompt")}
                    <p className="text-xs text-muted-foreground">
                      Provide instructions for how the agent should determine the phone number to transfer to.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Transfer Type</Label>
                  <Select value={transferType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cold">Cold Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Standard Technical Fields only for Custom Tools */}
            {selectedType === "custom" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="endpoint-url">Endpoint URL *</Label>
                  <Input
                    id="endpoint-url"
                    data-testid="tool-endpoint"
                    placeholder="https://api.example.com/endpoint"
                    value={endpointUrl}
                    onChange={(e) => setEndpointUrl(e.target.value)}
                    {...field("endpointUrl", "endpoint-url")}
                  />
                  {error("endpointUrl", "endpoint-url")}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="http-method">HTTP Method *</Label>
                  <Select value={httpMethod} onValueChange={setHttpMethod}>
                    <SelectTrigger id="http-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Headers</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddHeader}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Header
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {headers.map((header, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder="Key (e.g., Authorization)"
                          value={header.key}
                          onChange={(e) => handleHeaderChange(index, "key", e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Value (e.g., Bearer token)"
                          value={header.value}
                          onChange={(e) => handleHeaderChange(index, "value", e.target.value)}
                          className="flex-1"
                        />
                        {headers.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveHeader(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parameters">Parameters Schema (JSON)</Label>
                  <Textarea
                    id="parameters"
                    placeholder={'{\n  "properties": {\n    "query": {\n      "type": "string",\n      "description": "Search query"\n    }\n  },\n  "required": ["query"]\n}'}
                    value={parametersJson}
                    onChange={(e) => setParametersJson(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                    {...field("parameters", "parameters")}
                  />
                  {error("parameters", "parameters")}
                  <p className="text-xs text-muted-foreground">
                    Define the parameters this tool accepts (JSON Schema format). Must be valid JSON. Parameter names must start with a letter or underscore and only contain letters, numbers, and underscores.
                  </p>
                </div>
              </>
            )}

            {selectedType === "custom" && (
              <ToolOptionsFields idPrefix="add" runtime={runtime} toolName={toolName} value={toolOptions} onChange={setToolOptions} />
            )}

            {/* Timeout option only for Custom Tools and Send SMS (where relevant) */}
            {selectedType !== "end_call" && selectedType !== "transfer_call" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="timeout">Timeout (seconds)</Label>
                  <Input
                    id="timeout"
                    type="number"
                    value={timeoutSeconds}
                    onChange={(e) => setTimeoutSeconds(parseInt(e.target.value) || 30)}
                    placeholder="30"
                    min="5"
                    max="300"
                    {...field("timeout", "timeout")}
                  />
                  {error("timeout", "timeout")}
                </div>

              </>
            )}
          </div>
        )}

        {saveError && (
          <p role="alert" data-testid="tool-save-error" className="text-sm text-destructive">
            {saveError}
          </p>
        )}

        <DialogFooter>
          {/* Through handleOpenChange, like Escape and the close button, so the form resets. */}
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving} data-testid="tool-cancel">
            Cancel
          </Button>
          {selectedType && (
            <Button onClick={handleSubmit} disabled={saving} aria-busy={saving} data-testid="tool-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {saving ? "Adding…" : "Add Tool"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
